// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module DashboardBuilder
 * @package @geenius/analytics/solidjs-css
 * @description Renders the SolidJS CSS dashboard builder that composes
 * heterogeneous analytics widgets into a page-ready grid. The builder consumes
 * the shared dashboard layout vocabulary so consumers can declare dashboards as
 * data while preserving the standalone CSS implementation.
 */

import type {
  CohortDashboardWidget,
  DashboardLayout,
  DashboardWidget,
  FunnelDashboardWidget,
  RealtimeDashboardWidget,
  ReplayDashboardWidget,
} from "@geenius/analytics";
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@geenius/ui/solidjs-css";
import {
  type Component,
  createEffect,
  createMemo,
  createSignal,
  For,
  Show,
} from "solid-js";
import { createAnalyticsCopy } from "../lib/analyticsCopy";
import { cx } from "../lib/cx";
import { ChartCard } from "./ChartCard";
import { DeviceBreakdown } from "./DeviceBreakdown";
import { EventTable } from "./EventTable";
import { FunnelChart } from "./FunnelChart";
import { RealtimeBadge } from "./RealtimeBadge";
import { StatsCard } from "./StatsCard";
import { TopPagesTable } from "./TopPagesTable";

type AnalyticsCopy = ReturnType<typeof createAnalyticsCopy>;

/** Time range option shown by the SolidJS CSS dashboard builder toolbar. */
export interface DashboardBuilderTimeRangeOption {
  value: string;
  label: string;
}

const DEFAULT_TIME_RANGE_OPTIONS: DashboardBuilderTimeRangeOption[] = [
  { value: "24h", label: "24 hours" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
];

/**
 * Props for rendering the SolidJS CSS dashboard builder.
 *
 * @property layout Shared dashboard layout definition or a raw widget array.
 * @property editable Whether to render dashboard editing and reordering controls.
 * @property timeRange Controlled selected dashboard time range.
 * @property timeRangeOptions Options shown in the dashboard time-range control.
 * @property selectedWidgetId Controlled selected widget identifier for the editor.
 * @property onLayoutChange Called with the next widget order after reordering.
 * @property onTimeRangeChange Called when the user selects a new time range.
 * @property onWidgetSelect Called when the user opens a widget in the editor.
 * @property class Optional class name merged into the section wrapper.
 */
export interface DashboardBuilderProps {
  layout: DashboardLayout | DashboardWidget[];
  editable?: boolean;
  timeRange?: string;
  timeRangeOptions?: DashboardBuilderTimeRangeOption[];
  selectedWidgetId?: string;
  onLayoutChange?: (widgets: DashboardWidget[]) => void;
  onTimeRangeChange?: (timeRange: string) => void;
  onWidgetSelect?: (widget: DashboardWidget) => void;
  class?: string;
}

function moveWidget(
  widgets: DashboardWidget[],
  widgetId: string,
  offset: number,
): DashboardWidget[] {
  const fromIndex = widgets.findIndex((widget) => widget.id === widgetId);
  if (fromIndex < 0) return widgets;

  const toIndex = Math.max(0, Math.min(widgets.length - 1, fromIndex + offset));
  if (fromIndex === toIndex) return widgets;

  const next = [...widgets];
  const [moved] = next.splice(fromIndex, 1);
  if (!moved) return widgets;
  next.splice(toIndex, 0, moved);
  return next;
}

function moveWidgetToTarget(
  widgets: DashboardWidget[],
  draggedWidgetId: string,
  targetWidgetId: string,
): DashboardWidget[] {
  const fromIndex = widgets.findIndex(
    (widget) => widget.id === draggedWidgetId,
  );
  const toIndex = widgets.findIndex((widget) => widget.id === targetWidgetId);
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return widgets;

  const next = [...widgets];
  const [moved] = next.splice(fromIndex, 1);
  if (!moved) return widgets;
  next.splice(toIndex, 0, moved);
  return next;
}

function renderCohort(widget: CohortDashboardWidget, copy: AnalyticsCopy) {
  return (
    <Card class="gn-analytics-dashboard-widget gn-analytics-dashboard-widget--cohort">
      <CardHeader>
        <CardTitle
          class="gn-analytics-dashboard-widget__title"
          data-analytics-dashboard-widget-title
        >
          {widget.title}
        </CardTitle>
        <CardDescription>
          {copy.plural(
            "analytics.dashboardBuilder.cohortDescription",
            widget.analysis.intervals.length,
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div
          class="gn-analytics-dashboard-widget__meta"
          data-analytics-cohort-overview
        >
          <Badge variant="secondary">
            {copy.plural(
              "analytics.dashboardBuilder.cohorts",
              widget.analysis.cohorts.length,
            )}
          </Badge>
          <Badge variant="secondary" data-analytics-cohort-intervals>
            {copy.t("analytics.dashboardBuilder.dayIntervals", {
              intervals: widget.analysis.intervals.join(", "),
            })}
          </Badge>
        </div>
        <div class="gn-analytics-cohort__grid" data-analytics-cohort-grid>
          <For each={widget.analysis.cohorts}>
            {(cohort) => (
              <Card class="gn-analytics-cohort__card" shadow="none">
                <CardContent>
                  <strong>{cohort.cohort}</strong>
                  <div>
                    {copy.plural(
                      "analytics.dashboardBuilder.users",
                      cohort.size,
                    )}
                  </div>
                  <div
                    class="gn-analytics-cohort__retention"
                    data-analytics-cohort-retention
                  >
                    <For each={cohort.retained}>
                      {(point) => (
                        <span
                          class="gn-analytics-cohort__point"
                          data-analytics-cohort-point
                        >
                          <span>
                            {copy.t("analytics.dashboardBuilder.dayLabel", {
                              day: point.day,
                            })}
                          </span>
                          <span>{point.retentionRate.toFixed(1)}%</span>
                        </span>
                      )}
                    </For>
                  </div>
                </CardContent>
              </Card>
            )}
          </For>
        </div>
      </CardContent>
    </Card>
  );
}

function renderReplay(widget: ReplayDashboardWidget, copy: AnalyticsCopy) {
  return (
    <Card class="gn-analytics-dashboard-widget gn-analytics-dashboard-widget--replay">
      <CardHeader>
        <CardTitle
          class="gn-analytics-dashboard-widget__title"
          data-analytics-dashboard-widget-title
        >
          {widget.title}
        </CardTitle>
        <CardDescription>
          {copy.t("analytics.dashboardBuilder.replayDescription")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div
          class="gn-analytics-dashboard-widget__meta"
          data-analytics-session-replay-meta
        >
          <Badge variant="secondary">
            {copy.plural(
              "analytics.dashboardBuilder.frames",
              widget.replay.frames.length,
            )}
          </Badge>
          <Badge variant="secondary">
            {copy.t("analytics.dashboardBuilder.durationSeconds", {
              seconds: Math.round(widget.replay.duration / 1000),
            })}
          </Badge>
        </div>
        <ol
          class="gn-analytics-session-replay__list"
          data-analytics-session-replay-list
        >
          <For each={widget.replay.frames}>
            {(frame) => (
              <li
                class="gn-analytics-session-replay__frame"
                data-analytics-session-replay-frame
              >
                <div class="gn-analytics-session-replay__frame-header">
                  <strong>{frame.label}</strong>
                  <span class="gn-analytics-session-replay__frame-time">
                    {copy.formatTime(frame.timestamp)}
                  </span>
                </div>
                <span class="gn-analytics-session-replay__frame-url">
                  {frame.url}
                </span>
              </li>
            )}
          </For>
        </ol>
      </CardContent>
    </Card>
  );
}

function renderRealtime(widget: RealtimeDashboardWidget) {
  return (
    <Card class="gn-analytics-dashboard-widget gn-analytics-dashboard-widget--realtime">
      <CardHeader>
        <CardTitle
          class="gn-analytics-dashboard-widget__title"
          data-analytics-dashboard-widget-title
        >
          {widget.title}
        </CardTitle>
        <Show when={widget.description}>
          <CardDescription>{widget.description}</CardDescription>
        </Show>
      </CardHeader>
      <CardContent>
        <RealtimeBadge
          stats={widget.stats}
          isLoading={widget.isLoading ?? false}
        />
      </CardContent>
    </Card>
  );
}

function renderFunnel(widget: FunnelDashboardWidget) {
  return <FunnelChart result={widget.result} />;
}

function renderWidget(widget: DashboardWidget, copy: AnalyticsCopy) {
  switch (widget.kind) {
    case "metric":
      return (
        <StatsCard
          label={widget.title}
          value={widget.metric.value}
          change={
            widget.metric.changePercent !== undefined
              ? `${widget.metric.changePercent.toFixed(1)}%`
              : undefined
          }
          trend={
            widget.metric.trend === "flat" ? "neutral" : widget.metric.trend
          }
        />
      );
    case "chart":
      return (
        <ChartCard
          title={widget.title}
          data={widget.data}
          type={widget.chartType}
        />
      );
    case "top-pages":
      return <TopPagesTable pages={widget.pages} />;
    case "device-breakdown":
      return <DeviceBreakdown breakdown={widget.breakdown} />;
    case "events":
      return <EventTable events={widget.events} maxRows={widget.maxRows} />;
    case "funnel":
      return renderFunnel(widget);
    case "realtime":
      return renderRealtime(widget);
    case "cohort":
      return renderCohort(widget, copy);
    case "replay":
      return renderReplay(widget, copy);
    default:
      return null;
  }
}

/**
 * Renders the SolidJS CSS dashboard builder from a shared layout definition.
 *
 * @param props Dashboard layout data and an optional wrapper class.
 * @returns A section element containing the resolved analytics widget grid.
 */
export const DashboardBuilder: Component<DashboardBuilderProps> = (props) => {
  const resolved = createMemo(() =>
    Array.isArray(props.layout) ? { widgets: props.layout } : props.layout,
  );
  const [editableWidgets, setEditableWidgets] = createSignal<DashboardWidget[]>(
    resolved().widgets,
  );
  const [draggedWidgetId, setDraggedWidgetId] = createSignal<string | null>(
    null,
  );
  const [internalTimeRange, setInternalTimeRange] = createSignal(
    props.timeRange ?? props.timeRangeOptions?.[1]?.value ?? "7d",
  );
  const [internalSelectedWidgetId, setInternalSelectedWidgetId] = createSignal<
    string | null
  >(props.selectedWidgetId ?? null);
  const copy = createAnalyticsCopy();
  const timeRangeOptions = () =>
    props.timeRangeOptions ?? DEFAULT_TIME_RANGE_OPTIONS;
  const widgets = () =>
    props.editable ? editableWidgets() : resolved().widgets;
  const activeTimeRange = () => props.timeRange ?? internalTimeRange();
  const activeSelectedWidgetId = () =>
    props.selectedWidgetId ??
    internalSelectedWidgetId() ??
    widgets()[0]?.id ??
    null;
  const selectedWidget = () =>
    widgets().find((widget) => widget.id === activeSelectedWidgetId()) ?? null;

  createEffect(() => {
    setEditableWidgets(resolved().widgets);
  });

  createEffect(() => {
    if (props.timeRange !== undefined) {
      setInternalTimeRange(props.timeRange);
    }
  });

  createEffect(() => {
    if (props.selectedWidgetId !== undefined) {
      setInternalSelectedWidgetId(props.selectedWidgetId);
    }
  });

  const applyWidgetOrder = (nextWidgets: DashboardWidget[]): void => {
    setEditableWidgets(nextWidgets);
    props.onLayoutChange?.(nextWidgets);
  };

  const handleMoveWidget = (widgetId: string, offset: number): void => {
    applyWidgetOrder(moveWidget(widgets(), widgetId, offset));
  };

  const handleSelectWidget = (widget: DashboardWidget): void => {
    setInternalSelectedWidgetId(widget.id);
    props.onWidgetSelect?.(widget);
  };

  const handleTimeRangeChange = (nextTimeRange: string): void => {
    setInternalTimeRange(nextTimeRange);
    props.onTimeRangeChange?.(nextTimeRange);
  };

  return (
    <section
      class={cx("gn-analytics-dashboard-builder", props.class)}
      data-analytics-dashboard-builder
    >
      <Show when={resolved().title || resolved().description}>
        <header
          class="gn-analytics-dashboard-builder__header"
          data-analytics-dashboard-builder-header
        >
          <Show when={resolved().title}>
            <h2 class="gn-analytics-dashboard-builder__title">
              {resolved().title}
            </h2>
          </Show>
          <Show when={resolved().description}>
            <p class="gn-analytics-dashboard-builder__description">
              {resolved().description}
            </p>
          </Show>
        </header>
      </Show>
      <Show when={props.editable}>
        <div
          class="gn-analytics-dashboard-builder__toolbar"
          data-analytics-dashboard-builder-toolbar
        >
          <label class="gn-analytics-dashboard-builder__time-range">
            <span>{copy.t("analytics.dashboardBuilder.timeRangeAria")}</span>
            <select
              class="gn-analytics-dashboard-builder__select"
              value={activeTimeRange()}
              onChange={(event) =>
                handleTimeRangeChange(event.currentTarget.value)
              }
            >
              <For each={timeRangeOptions()}>
                {(option) => (
                  <option value={option.value}>{option.label}</option>
                )}
              </For>
            </select>
          </label>
          <span class="gn-analytics-dashboard-builder__toolbar-summary">
            {copy.plural(
              "analytics.dashboardBuilder.widgets",
              widgets().length,
            )}
          </span>
        </div>
      </Show>
      <div
        class="gn-analytics-dashboard-builder__grid"
        data-analytics-dashboard-builder-grid
      >
        <For each={widgets()}>
          {(widget, index) => (
            <fieldset
              class={cx(
                "gn-analytics-dashboard-builder__cell",
                props.editable &&
                  "gn-analytics-dashboard-builder__cell--editable",
                activeSelectedWidgetId() === widget.id &&
                  "gn-analytics-dashboard-builder__cell--selected",
              )}
              data-analytics-dashboard-builder-cell
              data-span={widget.span ?? 1}
              draggable={props.editable}
              onDragStart={() => setDraggedWidgetId(widget.id)}
              onDragOver={(event) => {
                if (props.editable) event.preventDefault();
              }}
              onDrop={(event) => {
                event.preventDefault();
                const draggedId = draggedWidgetId();
                if (draggedId) {
                  applyWidgetOrder(
                    moveWidgetToTarget(widgets(), draggedId, widget.id),
                  );
                }
                setDraggedWidgetId(null);
              }}
            >
              <legend class="gn-analytics-a11y__sr-only">
                {copy.t("analytics.dashboardBuilder.widgetLegend", {
                  title: widget.title,
                })}
              </legend>
              <Show when={props.editable}>
                <div
                  class="gn-analytics-dashboard-builder__cell-controls"
                  data-analytics-dashboard-widget-controls
                >
                  <button
                    type="button"
                    class="gn-analytics-dashboard-builder__control"
                    disabled={index() === 0}
                    onClick={() => handleMoveWidget(widget.id, -1)}
                  >
                    {copy.t("analytics.dashboardBuilder.moveWidgetUp", {
                      title: widget.title,
                    })}
                  </button>
                  <button
                    type="button"
                    class="gn-analytics-dashboard-builder__control"
                    disabled={index() === widgets().length - 1}
                    onClick={() => handleMoveWidget(widget.id, 1)}
                  >
                    {copy.t("analytics.dashboardBuilder.moveWidgetDown", {
                      title: widget.title,
                    })}
                  </button>
                  <button
                    type="button"
                    class="gn-analytics-dashboard-builder__control"
                    onClick={() => handleSelectWidget(widget)}
                  >
                    {copy.t("analytics.dashboardBuilder.editWidget", {
                      title: widget.title,
                    })}
                  </button>
                </div>
              </Show>
              {renderWidget(widget, copy)}
            </fieldset>
          )}
        </For>
      </div>
      <Show when={props.editable && selectedWidget()}>
        {(widget) => (
          <aside
            class="gn-analytics-dashboard-builder__editor"
            data-analytics-dashboard-widget-editor
            role="complementary"
            aria-label={copy.t("analytics.dashboardBuilder.widgetEditor")}
          >
            <h3 class="gn-analytics-dashboard-builder__editor-title">
              {copy.t("analytics.dashboardBuilder.widgetEditor")}
            </h3>
            <dl class="gn-analytics-dashboard-builder__editor-details">
              <div>
                <dt>{copy.t("analytics.dashboardBuilder.titleLabel")}</dt>
                <dd>{widget().title}</dd>
              </div>
              <div>
                <dt>{copy.t("analytics.dashboardBuilder.kindLabel")}</dt>
                <dd>{widget().kind}</dd>
              </div>
              <div>
                <dt>{copy.t("analytics.dashboardBuilder.spanLabel")}</dt>
                <dd>{widget().span ?? 1}</dd>
              </div>
              <Show when={widget().description}>
                <div>
                  <dt>
                    {copy.t("analytics.dashboardBuilder.descriptionLabel")}
                  </dt>
                  <dd>{widget().description}</dd>
                </div>
              </Show>
            </dl>
          </aside>
        )}
      </Show>
    </section>
  );
};
